import { Box, FormHelperText, useMediaQuery, useTheme } from '@mui/material';
import DateRangePicker from 'rsuite/DateRangePicker';
import { CustomProvider } from 'rsuite';
import plPL from 'rsuite/locales/pl_PL';
import enUS from 'rsuite/locales/en_US';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isAfter, isValid, startOfDay, subDays } from 'date-fns';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useLocaleState, useTranslate } from 'react-admin';
import { parseDateValue, toIsoDateString } from '@/helpers/date/dateValue';

const getToday = () => startOfDay(new Date());
const getYesterday = () => startOfDay(subDays(new Date(), 1));

const VALIDATION_KEYS_BY_MAX_DATE = {
    yesterday: 'common.dateRange.validation.notLaterThanYesterday',
    today: 'common.dateRange.validation.notLaterThanToday',
};

const resolveMaxDate = maxDate => {
    if (maxDate === 'today') return getToday();
    if (maxDate === 'yesterday') return getYesterday();
    return null;
};

const isAfterMaxDate = (date, maxSelectableDate) => !!maxSelectableDate && isValid(date) && isAfter(startOfDay(date), maxSelectableDate);

const isCompleteValidRange = range => !!range?.[0] && !!range?.[1] && isValid(range[0]) && isValid(range[1]);

export const DateRangeInput = ({ onChange, start, end, label, disabled = false, showOneCalendar = false, maxDate = null }) => {
    const translate = useTranslate();
    const [currentLocale] = useLocaleState();
    const defaultLabel = label || translate('common.dateRange.label');
    const [open, setOpen] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [invalidDraft, setInvalidDraft] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isPolishLocale = currentLocale?.toLowerCase().startsWith('pl');
    const pickerLocale = isPolishLocale ? plPL : enUS;
    const locale = {
        ok: translate('common.filter.applyFilter'),
    };

    const today = useMemo(() => getToday(), []);
    const yesterday = useMemo(() => getYesterday(), []);
    const maxSelectableDate = useMemo(() => resolveMaxDate(maxDate), [maxDate]);

    const defaultStart = yesterday;
    const defaultEnd = maxDate === 'today' ? today : yesterday;
    const startDate = parseDateValue(start) || defaultStart;
    const endDate = parseDateValue(end) || defaultEnd;
    const committedValue = [startDate, endDate];
    const pickerValue = invalidDraft || committedValue;
    const hasError = !!validationError;

    useEffect(() => {
        setInvalidDraft(null);
        setValidationError(null);
    }, [start, end]);

    const shouldDisableDate = useMemo(() => {
        if (!maxSelectableDate) {
            return undefined;
        }
        return DateRangePicker.after(maxSelectableDate);
    }, [maxSelectableDate]);

    const handleDateRangeChange = useCallback(
        range => {
            if (!isCompleteValidRange(range)) {
                return;
            }

            // Reject future / after-max typed dates — do not propagate to filters.
            if (isAfterMaxDate(range[0], maxSelectableDate) || isAfterMaxDate(range[1], maxSelectableDate)) {
                setInvalidDraft(range);
                const validationKey = VALIDATION_KEYS_BY_MAX_DATE[maxDate] ?? null;
                setValidationError(validationKey ? translate(validationKey) : null);
                return;
            }

            setInvalidDraft(null);
            setValidationError(null);
            setOpen(false);
            if (typeof onChange === 'function') {
                onChange({ start: toIsoDateString(range[0]), end: toIsoDateString(range[1]) });
            }
        },
        [maxDate, maxSelectableDate, onChange, translate],
    );

    const handlePickerState = useCallback(
        isOpen => {
            const pickerElement = document.querySelector('.rs-picker .rs-input-group.rs-input-group-inside');
            const dialog = document.querySelector('.custom-dialog-right');
            if (pickerElement) {
                if (isOpen && !hasError) {
                    pickerElement.style.border = `2px solid ${Palette.gray.gray900}`;
                    pickerElement.style.outline = `2px solid ${Palette.yellow.yellow200}`;
                } else {
                    pickerElement.style.border = '';
                    pickerElement.style.outline = '';
                    if (dialog) {
                        dialog.style.overflow = '';
                    }
                    setOpen(false);
                }
            }

            if (!isOpen && invalidDraft) {
                setInvalidDraft(null);
                setValidationError(null);
            }
        },
        [hasError, invalidDraft],
    );

    const handlePickerClick = useCallback(() => {
        setOpen(true);
        const dialog = document.querySelector('.custom-dialog-right');
        if (dialog) {
            dialog.style.overflow = 'hidden';
        }
        if (open) {
            setOpen(false);
            handlePickerState(false);
            if (dialog) {
                dialog.style.overflow = '';
            }
        }
    }, [open, handlePickerState]);

    return (
        <Box
            className='range-picker'
            sx={{
                position: 'relative',
                ...(hasError && {
                    '& .rs-picker .rs-input-group.rs-input-group-inside': {
                        border: `2px solid ${Palette.red.red500} !important`,
                        outline: 'none !important',
                    },
                }),
            }}
        >
            <span className='range-picker-label'>{defaultLabel}</span>
            <CustomProvider locale={pickerLocale}>
                <DateRangePicker
                    value={pickerValue}
                    character=' - '
                    showHeader={false}
                    ranges={[]}
                    onChange={handleDateRangeChange}
                    onOpen={() => handlePickerState(true)}
                    onClose={() => handlePickerState(false)}
                    open={open}
                    onClick={handlePickerClick}
                    format='dd.MM.yyyy'
                    locale={locale}
                    showOneCalendar={showOneCalendar || isMobile}
                    disabled={disabled}
                    shouldDisableDate={shouldDisableDate}
                />
            </CustomProvider>
            {validationError && (
                <FormHelperText error sx={{ mx: '14px', mt: '4px' }}>
                    {validationError}
                </FormHelperText>
            )}
        </Box>
    );
};

DateRangeInput.propTypes = {
    onChange: PropTypes.func,
    start: PropTypes.string,
    end: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    showOneCalendar: PropTypes.bool,
    maxDate: PropTypes.oneOf(['today', 'yesterday']),
};
